import React, { useMemo } from 'react';
import { MapPin } from 'lucide-react';
import { slug, eur } from './donnees';

const REGIONS = [
  { nom: 'Antilles', territoires: ['Guadeloupe', 'Martinique', 'Saint-Martin', 'Saint-Barthélemy'] },
  { nom: 'Amérique du Sud', territoires: ['Guyane'] },
  { nom: 'Océan Indien', territoires: ['La Réunion', 'Mayotte'] },
  { nom: 'Atlantique Nord', territoires: ['Saint-Pierre-et-Miquelon'] },
  { nom: 'Europe', territoires: ['France métropolitaine'] },
];
const TEINTES = ['#e0e7ff', '#a5b4fc', '#6366f1', '#3730a3', '#1e1b4b'];

const teinte = (v, max) => (!v ? '#f1f5f9' : TEINTES[Math.min(TEINTES.length - 1, Math.floor((v / max) * TEINTES.length))]);

function parTerritoire(lignes, mesure) {
  const m = new Map();
  lignes.forEach((l) => {
    const t = m.get(l.territoire) || { ids: new Set(), ca: new Map() };
    t.ids.add(l.id);
    t.ca.set(l.id, l.ca);
    m.set(l.territoire, t);
  });
  return new Map([...m].map(([k, t]) => [k, mesure === 'ca' ? [...t.ca.values()].reduce((s, v) => s + v, 0) : t.ids.size]));
}

const Tuile = ({ nom, valeur, max, mesure, actif, onClick }) => {
  const fond = teinte(valeur, max);
  const clair = !valeur || TEINTES.indexOf(fond) < 2;
  return (
    <button type="button" onClick={onClick} data-testid={`carte-territoire-${slug(nom)}`}
      className={`group flex min-h-[86px] flex-col justify-between rounded-xl p-3 text-left transition-transform duration-200 hover:-translate-y-0.5 ${actif ? 'ring-2 ring-amber-400 ring-offset-2' : ''}`}
      style={{ backgroundColor: fond, color: clair ? '#1e293b' : '#fff' }}>
      <span className="flex items-center gap-1 text-xs font-semibold"><MapPin className="h-3.5 w-3.5 opacity-70" />{nom}</span>
      <span className={`${mesure === 'ca' ? 'text-base' : 'text-xl'} font-bold tabular-nums break-words`} data-testid={`carte-valeur-${slug(nom)}`}>{mesure === 'ca' ? eur(valeur || 0) : (valeur || 0)}</span>
      <span className="text-[10px] opacity-70">{mesure === 'ca' ? 'CA des contrats' : 'contrat(s)'}</span>
    </button>
  );
};

export default function CarteTerritoires({ lignes, mesure, selection, onToggle }) {
  const valeurs = useMemo(() => parTerritoire(lignes, mesure), [lignes, mesure]);
  const max = Math.max(1, ...valeurs.values());
  const connus = REGIONS.flatMap((r) => r.territoires);
  const autres = [...valeurs.keys()].filter((t) => !connus.includes(t));
  const regions = autres.length ? [...REGIONS, { nom: 'Autres', territoires: autres }] : REGIONS;
  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="stats-carte-territoires">
      <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h3 className="text-base font-semibold text-slate-900">Carte des territoires</h3>
          <p className="text-xs text-slate-500">Contrats retenus par territoire de ventilation du CA · cliquez pour filtrer</p>
        </div>
        <div className="flex items-center gap-1 text-[10px] text-slate-500" data-testid="carte-legende">
          <span>0</span>{['#f1f5f9', ...TEINTES].map((c) => <span key={c} className="h-3 w-6 rounded-sm" style={{ backgroundColor: c }} />)}
          <span>{mesure === 'ca' ? eur(max) : max}</span>
        </div>
      </div>
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-6">
        {regions.map((r) => (
          <div key={r.nom} className={`rounded-xl border border-slate-100 bg-slate-50/60 p-3 ${r.territoires.length > 1 ? 'xl:col-span-2' : ''}`}>
            <p className="mb-2 text-[10px] font-semibold uppercase tracking-widest text-slate-400">{r.nom}</p>
            <div className={`grid gap-2 ${r.territoires.length > 1 ? 'grid-cols-2' : ''}`}>
              {r.territoires.map((t) => (
                <Tuile key={t} nom={t} valeur={valeurs.get(t)} max={max} mesure={mesure} actif={selection.includes(t)} onClick={() => onToggle(t)} />
              ))}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}
